import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import ComingSoonPopup from './ComingSoonPopup';
import {
  IconMark,
  IconYouTube,
  IconDiscord,
  IconGithub,
  IconLinkedIn,
} from './Icons';

interface FooterProps {
  descriptionKey?: string;
}

export default function Footer({ descriptionKey = 'landing:footer.description' }: FooterProps) {
  const { t } = useTranslation(['common', 'landing', 'pricing']);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  const soon = () => setShowComingSoonPopup(true);

  const columns: Array<[string, Array<[string, () => void, boolean]>]> = [
    [
      t('common:footer.sections.product', 'Product'),
      [
        [t('common:footer.links.pricing', 'Pricing'), () => navigate('/pricing'), false],
        [t('common:footer.links.templates', 'Templates'), soon, true],
        [t('common:footer.links.changelog', 'Changelog'), soon, true],
        [t('common:footer.links.roadmap', 'Roadmap'), soon, true],
      ],
    ],
    [
      t('common:footer.sections.resources', 'Resources'),
      [
        [t('common:footer.links.documentation', 'Documentation'), () => navigate('/docs'), false],
        [t('common:footer.links.tutorials', 'Tutorials'), soon, true],
        [t('common:footer.links.community', 'Community'), soon, true],
        [t('common:footer.links.support', 'Support'), soon, true],
      ],
    ],
    [
      t('common:footer.sections.company', 'Company'),
      [
        [t('common:footer.links.aboutUs', 'About'), soon, true],
        [t('common:footer.links.careers', 'Careers'), soon, true],
        [t('common:footer.links.contact', 'Contact'), soon, true],
        [t('common:footer.links.privacy', 'Privacy'), soon, true],
        [t('common:footer.links.terms', 'Terms'), soon, true],
      ],
    ],
  ];

  return (
    <>
      <footer
        style={{
          padding: isMobile ? '40px 16px 28px' : '56px 80px 32px',
          borderTop: '1px solid var(--line)',
          background: 'var(--bg)',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1.6fr 1fr 1fr 1fr', gap: isMobile ? 28 : 56, marginBottom: isMobile ? 32 : 48 }}>
          <div style={isMobile ? { gridColumn: '1 / -1' } : undefined}>
            <a href="/" className="brand" style={{ fontSize: 18, marginBottom: 16, display: 'inline-flex' }}>
              <IconMark size={22} />
              {t('common:app.name', 'Entire')}
            </a>
            <p className="muted-2" style={{ fontSize: 13.5, lineHeight: 1.6, margin: '12px 0 0', maxWidth: 320 }}>
              {t(descriptionKey)}
            </p>
          </div>

          {columns.map(([heading, links]) => (
            <div key={heading}>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 13,
                  color: 'var(--text)',
                  marginBottom: 16,
                  letterSpacing: '-0.005em',
                }}
              >
                {heading}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {links.map(([label, onClick]) => (
                  <li key={label}>
                    <button
                      type="button"
                      onClick={onClick}
                      className="nav-link"
                      style={{
                        height: 'auto',
                        padding: 0,
                        color: 'var(--text-5)',
                        fontWeight: 400,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="hairline" />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            paddingTop: 22,
          }}
        >
          <div className="muted" style={{ fontSize: 12.5 }}>
            {t('common:footer.copyright', '© 2026 Entire Inc.')} &nbsp;·&nbsp;{' '}
            <span className="mono" style={{ fontSize: 11.5 }}>v{__APP_VERSION__} · Beta</span>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button type="button" onClick={soon} className="nav-link" style={{ width: 30, height: 30, padding: 0, justifyContent: 'center', background: 'transparent', border: 'none' }} title="YouTube">
              <IconYouTube />
            </button>
            <button type="button" onClick={soon} className="nav-link" style={{ width: 30, height: 30, padding: 0, justifyContent: 'center', background: 'transparent', border: 'none' }} title="Discord">
              <IconDiscord />
            </button>
            <button type="button" onClick={soon} className="nav-link" style={{ width: 30, height: 30, padding: 0, justifyContent: 'center', background: 'transparent', border: 'none' }} title="GitHub">
              <IconGithub />
            </button>
            <button type="button" onClick={soon} className="nav-link" style={{ width: 30, height: 30, padding: 0, justifyContent: 'center', background: 'transparent', border: 'none' }} title="LinkedIn">
              <IconLinkedIn />
            </button>
          </div>
        </div>
      </footer>

      <ComingSoonPopup
        isOpen={showComingSoonPopup}
        onClose={() => setShowComingSoonPopup(false)}
      />
    </>
  );
}
